import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import type { Id } from '../../convex/_generated/dataModel'

/**
 * 1:1 support chat — mirrors useCommunityChat but for a single thread between
 * the user and the AI intake agent (with optional human/founder handoff).
 *
 * Realtime: the AI reply is scheduled server-side by sendMessage and arrives
 * through the live `listMyMessages` query — no client polling.
 */
export function useSupportChat(open: boolean) {
  const { user, isLoading: isAuthLoading } = useAuth()
  // Anonymous users can't open a support thread (the backend requires a real
  // identity to follow up), so the panel shows them a sign-in prompt instead.
  const signedIn = !!user && !user.isAnonymous

  const thread = useQuery(api.support.myThread, signedIn ? {} : 'skip')
  const threadId = thread?._id ?? null

  const messages = useQuery(
    api.support.listMyMessages,
    threadId ? { threadId } : 'skip',
  )

  const getOrCreateThread = useMutation(api.support.getOrCreateThread)
  const sendMutation = useMutation(api.support.sendMessage)

  // Ensure a thread exists once the panel is opened by a signed-in user, so the
  // realtime message query attaches immediately (before the first send).
  const ensuredRef = useRef(false)
  useEffect(() => {
    if (!open) {
      ensuredRef.current = false
      return
    }
    if (!signedIn || ensuredRef.current) return
    if (thread === undefined) return // myThread still loading
    if (thread) {
      ensuredRef.current = true
      return
    }
    ensuredRef.current = true
    getOrCreateThread().catch(() => {
      ensuredRef.current = false
    })
  }, [open, signedIn, thread, getOrCreateThread])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return
      let id: Id<'supportThreads'> | null = threadId
      if (!id) id = await getOrCreateThread()
      if (!id) return
      await sendMutation({ threadId: id, content })
    },
    [threadId, getOrCreateThread, sendMutation],
  )

  // The intake agent is mid-reply when the latest message is the user's own —
  // no AI/founder turn has landed yet. Used to block a second send so we don't
  // race two agent runs (which could double-file an issue).
  const msgs = messages ?? []
  const agentBusy = msgs.length > 0 && msgs[msgs.length - 1].role === 'user'

  return {
    signedIn,
    thread: thread ?? null,
    messages: messages ?? [],
    needsHuman: thread?.needsHuman ?? false,
    agentBusy,
    sendMessage,
    // Loading while auth resolves, the thread is being fetched, or messages for
    // an existing thread haven't arrived yet.
    isLoading:
      isAuthLoading ||
      (signedIn && thread === undefined) ||
      (!!threadId && messages === undefined),
  }
}
